'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useAui } from '@assistant-ui/react';
import { debounce } from 'lodash';

import { SYNC_EVENTS } from '@/lib/sync/events';
import { useSyncEvent } from '@/lib/sync/hooks/use-sync-event';
import { findThread, isLocalThreadId } from '@/lib/utils/thread';

const THREAD_LIST_REFRESH_DEBOUNCE = 300;

export function CrossTabSyncHandler() {
  const api = useAui();
  const apiRef = useRef(api);
  apiRef.current = api;
  const refreshThreadListRef = useRef<ReturnType<typeof debounce>>(undefined);

  useEffect(() => {
    // Refresh the sidebar via the runtime's own thread-list reload — a light re-fetch of
    // titles/order that must NOT blank or re-render the chat: it fires on every run-end in every
    // OTHER tab, so anything heavier would disrupt siblings that aren't even on the finished thread.
    refreshThreadListRef.current = debounce(async () => {
      // Skip while this tab's active thread is a locally-created (optimistic) one not yet in the
      // server list: reload() would replace its sidebar row with the remote item and orphan the
      // active highlight (main still points at the local id). This tab already shows the thread; it
      // reconciles on the next real thread switch.
      if (isLocalThreadId(apiRef.current.threads.getState().mainThreadId)) return;
      await apiRef.current.threads.reload();
    }, THREAD_LIST_REFRESH_DEBOUNCE);

    return () => {
      refreshThreadListRef.current?.cancel();
    };
  }, []);

  const refreshThreadList = useCallback(() => {
    refreshThreadListRef.current?.();
  }, []);

  const handleArchive = useCallback(
    (threadId: string) => {
      try {
        const { threadItems } = api.threads.getState();
        const thread = findThread(threadItems, threadId);
        if (thread) {
          api.threads.item({ id: thread.id }).archive();
        }
      } catch {
        // Thread may not exist in this tab's resources - safe to ignore
      }
    },
    [api],
  );

  const handleUnarchive = useCallback(
    (threadId: string) => {
      try {
        const { threadItems } = api.threads.getState();
        const thread = findThread(threadItems, threadId);
        if (thread) {
          api.threads.item({ id: thread.id }).unarchive();
        }
      } catch {
        // Thread may not exist in this tab's resources - safe to ignore
      }
    },
    [api],
  );

  // Another tab hit stop on a thread we're tailing (a resumed run) — cancel our run too so both
  // tabs settle on the same partial reply, instead of this tab spinning until the stream drops.
  const handleStreamStopped = useCallback(
    ({ threadUuid }: { threadUuid: string }) => {
      try {
        const { mainThreadId, threadItems } = api.threads.getState();
        const activeUuid = mainThreadId ? findThread(threadItems, mainThreadId)?.remoteId : null;
        const isRunning = api.thread.getState().isRunning;
        if (activeUuid === threadUuid && isRunning) {
          api.thread.cancelRun();
        }
      } catch {
        // swallow: cross-tab stream-stop handling is best-effort
      }
    },
    [api],
  );

  useSyncEvent(SYNC_EVENTS.THREAD_ARCHIVE, handleArchive);
  useSyncEvent(SYNC_EVENTS.THREAD_UNARCHIVE, handleUnarchive);
  useSyncEvent(SYNC_EVENTS.RUNTIME_FINISH, refreshThreadList);
  useSyncEvent(SYNC_EVENTS.RUNTIME_ERROR, refreshThreadList);
  useSyncEvent(SYNC_EVENTS.RESUMABLE_STREAM_STOPPED, handleStreamStopped);

  return null;
}
